import { ModalCancelOrderMottu } from '@components/Modals/CancelMottu'
import { useWhatsAppBot } from '@hooks/useWhatsAppBot'
import {
  ProfileFormPayment,
  ProfileOptions,
} from '@whatsmenu/entities/dist/profile'
import i18n from '@whatsmenu/i18n'
import { DateTime } from 'luxon'
import { useSession } from 'next-auth/react'
import React, {
  FormHTMLAttributes,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react'
import { Button, ButtonProps, Spinner } from 'react-bootstrap'
import { api, keetaIntegratorApi, nineNineIntegratorApi } from 'src/lib/axios'
import { AppContext } from '../../context/app.ctx'
import { CartsContext } from '../../context/cart.ctx'
import useLocalStorage from '../../hooks/useLocalStorage'
import Cart from '../../types/cart'
import { encodeTextURL } from '../../utils/wm-functions'
import { RefuseOrderModal } from '@components/Modals/ifood'
import { GiFullMotorcycleHelmet } from 'react-icons/gi'
import { canceledMessage } from '@utils/canceled-messages'
import { NineNineOrderCancelModal } from '@components/Modals/NineNine/nine-nine-order-cancel'
import { CancelPdvModal } from '@components/Modals/Requests/CancelPdvModal'
import { authConfig, cancelOrder } from '@whatsmenu/entregasexpressas'
import { AsaasRefundConfirm } from '@components/Modals/AsaasRefundConfirm'

type MetaTemplate = {
  id: string
  name: string
  language: string
  status: 'APPROVED' | 'PENDING_RENEWAL' | 'REJECTED' | 'DISABLED' | string
  category: string
  quality_score?: string
  rejected_reason?: string
}

type StatusTemplates = {
  production?: MetaTemplate
  transport?: MetaTemplate
  delivered?: MetaTemplate
  canceled?: MetaTemplate
}

let metaTemplatesCache: StatusTemplates | null = null
let metaTemplatesRequest: Promise<StatusTemplates> | null = null

type SendStatusMessageFormProps = {
  button: { name: string; props?: ButtonProps }
  taxmessageCancelMottu?: boolean
  cart: Cart
  newStatus?:
    | null
    | 'production'
    | 'transport'
    | 'motoboy'
    | 'canceled'
    | 'delivered'
  profileOptions?: ProfileOptions
  abbreviate?: boolean
} & FormHTMLAttributes<HTMLFormElement>

interface Reason {
  cancellationCode: string
  reason?: string
}

export const SendStatusMessageForm = ({
  button,
  cart,
  newStatus,
  profileOptions,
  abbreviate = false,
  ...rest
}: SendStatusMessageFormProps) => {
  const { data: session } = useSession()
  const {
    door,
    lastRequestDate,
    profile,
    handleShowToast,
    setRequestsToPackage,
    requestsToPackage,
    possibleMobile,
    currency,
  } = useContext(AppContext)
  const { setCarts, carts, setCart } = useContext(CartsContext)
  const [showReasons, setShowReasons] = useState(false)
  const [reasons, setReasons] = useState<Reason[]>([])
  const { setNewPlacedOrders, motoboys } = useContext(CartsContext)
  const [isClicked, setIsClicked] = useState(false)
  const [message, setMessage] = useState(
    (profileOptions ?? profile.options).placeholders.clientText
  )

  const [loading, setLoading] = useState(false)
  const [statusTemplates, setStatusTemplates] = useState<StatusTemplates>({})
  const [templatesLoaded, setTemplatesLoaded] = useState(false)
  const [closeTabs, setCloseTabs] = useLocalStorage(
    '@wmstatus:closeTabs',
    localStorage.getItem('@wmstatus:closeTabs') ?? false
  )
  const [showModalNineNine, setShowModalNineNine] = useState(false)
  const [pendingCancellationSkipMessage, setPendingCancellationSkipMessage] = useState(false)

  const { sendMessage, onMessageSend } = useWhatsAppBot()

  const [modalCancelDeliveryMottu, setModalCancelDeliveryMottu] = useState(false)
  const [taxmessageCancelMottu, setTaxmessageCancelMottu] = useState(false)
  const [showCancelNoSlugModal, setShowCancelNoSlugModal] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [showRefundConfirmModal, setShowRefundConfirmModal] = useState(false)

  const loadMetaTemplates = useCallback(async () => {
    const activeProfile = profileOptions ?? profile.options
    const { whatsappOficial } = activeProfile

    if (!whatsappOficial) {
      setTemplatesLoaded(true)
      setStatusTemplates({})
      return
    }

    if (metaTemplatesCache) {
      setStatusTemplates(metaTemplatesCache)
      setTemplatesLoaded(true)
      return
    }

    if (metaTemplatesRequest) {
      try {
        const templates = await metaTemplatesRequest
        setStatusTemplates(templates)
      } catch (error) {
        console.error('Erro ao carregar templates Meta WhatsApp:', error)
      } finally {
        setTemplatesLoaded(true)
      }
      return
    }

    metaTemplatesRequest = (async () => {
      const { data } = await api.get('/dashboard/integrations/whatsapp/meta/templates')
      const templates: MetaTemplate[] = data.templates || []

      const statusMap: StatusTemplates = {}
      templates.forEach((template) => {
        if (template.name.includes('status_production')) {
          statusMap.production = template
        } else if (template.name.includes('status_transport')) {
          statusMap.transport = template
        } else if (template.name.includes('status_delivered')) {
          statusMap.delivered = template
        } else if (template.name.includes('status_canceled')) {
          statusMap.canceled = template
        }
      })

      metaTemplatesCache = statusMap
      return statusMap
    })()

    try {
      const templates = await metaTemplatesRequest
      setStatusTemplates(templates)
    } catch (error) {
      console.error('Erro ao carregar templates Meta WhatsApp:', error)
    } finally {
      setTemplatesLoaded(true)
      metaTemplatesRequest = null
    }
  }, [profile, profileOptions])

  useEffect(() => {
    if (templatesLoaded) return
    loadMetaTemplates()
  }, [loadMetaTemplates, templatesLoaded])

  const handleReadyForPickUp = async ({ cart }: { cart: Cart }) => {
    try {
      switch (cart.origin) {
        case 'nine-nine':
          await nineNineIntegratorApi.post(`/orders/ready/${cart?.orderId}`)
          cart.status = 'transport'
          setCart(cart)
          break
        case 'keeta':
          await keetaIntegratorApi.post(`orders/update/${cart?.orderId}`, {
            dispatchType: 'DISPATCHED',
          })
          break
      }
    } catch (error) {
      console.error(
        'Erro ao atualizar o pedido para pronto para retirada',
        error
      )
      throw error
    }
  }

  const handleChangeStatus = async (e: React.MouseEvent<HTMLButtonElement>) => {
    console.log('evento chegando', cart.status)
    if (cart.origin === 'nine-nine' && newStatus === 'canceled') {
      setShowModalNineNine(true)
    }
    if (cart.origin === 'whatsmenu') {
      if(newStatus === 'canceled' && shouldAskForRefund()){
        setShowRefundConfirmModal(true)
      }else{

        handleSendMessage(e)
      }
    }
    if (cart.origin === 'ifood' && newStatus === 'canceled' && cart.orderId) {
      await cancellationReasons({ orderId: cart.orderId, cartOrigin: 'ifood' })
    }

    if (cart.origin === 'ifood' || cart.origin === 'keeta') {
      handleSendMessage(e)
    }

    if (cart.origin == 'keeta' && newStatus === 'transport' && cart.orderId) {
      await handleReadyForPickUp({ cart })
      cart.status = 'transport'
      setCart(cart)
    }

    if (
      cart.origin == 'nine-nine' &&
      newStatus === 'transport' &&
      cart.orderId
    ) {
      await nineNineIntegratorApi.post(`orders/ready/${cart?.orderId}`)
      cart.status = 'transport'
      setCart(cart)
    }
    if (
      cart.origin == 'nine-nine' &&
      newStatus === 'delivered' &&
      cart.orderId
    ) {
      try {
        if (cart.status === 'transport') {
          await nineNineIntegratorApi.post(`orders/delivered/${cart?.orderId}`)
          cart.status = 'delivered'
          setCart(cart)
        } else {
          handleShowToast({
            type: 'erro',
            content: 'Não foi possivel alterar status para concluido.',
            title: 'Pedido Concluido.',
          })
        }
      } catch (error) {
        console.error('Erro ao marcar pedido como entregue no 99Food', error)
      }
    }
  }

  const cancellationReasons = async ({
    orderId,
    cartOrigin,
  }: {
    orderId: string | undefined
    cartOrigin: Cart['origin']
  }) => {
    try {
      const { data } = await api.post(
        `/dashboard/ifood/order/${orderId}/cancellationReasons`,
        { cartOrigin }
      )

      data.forEach((reasons: any) => {
        if (reasons) {
          reasons.cancellationCode = reasons.cancelCodeId
          reasons.reason = reasons.description
          delete reasons.cancelCodeId
          delete reasons.description
        }
      })
      setReasons(data)
      setShowReasons(true)
    } catch (error) {
      console.error('erro ao cancelar o pedido no ifood', error)
    }
  }

  const handleCancelWithMessage = async () => {
    await processCancellation(false)
  }

  const handleCancelWithoutMessage = async () => {
    await processCancellation(true)
  }

  const processCancellation = async (skipMessage: boolean, refundAsaas = false) => {
    if (!buttonRef.current) return

    try {
      setLoading(true)
      buttonRef.current.disabled = true

      const oldStatus = cart.status

      await cart.updateStatus('canceled', session, refundAsaas)

      cart.status = 'canceled'
      setCart(cart)

      if (!skipMessage && oldStatus !== 'canceled') {
        const phoneMessage = canceledMessage(profile.options.locale.language)
        sendToWhatsAppHelper(phoneMessage)
      }

      setCarts([...carts])

      handleShowToast({
        type: 'success',
        content: skipMessage
          ? 'Pedido cancelado sem envio de mensagem'
          : 'Pedido cancelado com sucesso',
        title: 'Cancelamento',
      })
    } catch (error) {
      console.error('Erro ao cancelar pedido:', error)
      handleShowToast({
        type: 'erro',
        content: 'Erro ao cancelar o pedido',
        title: 'Erro',
      })
    } finally {
      if (buttonRef.current) {
        buttonRef.current.disabled = false
      }
      setLoading(false)
      setIsClicked(false)
    }
  }

  const shouldAskForRefund = (): boolean => {
    const hasAsaasRefundEnabled = profile.options.asaas?.allowRefund === true
    const isPaidOnline = cart.statusPayment === 'paid'
    const hasAsaasPayment = cart.formsPayment.some(
      (payment: any) => payment.payment === 'pix' || payment.payment === 'credit'
    )

    return hasAsaasRefundEnabled && isPaidOnline && hasAsaasPayment
  }

  const sendToWhatsAppHelper = (phoneMessage: string) => {
    if (!buttonRef.current) return

    const { linkWhatsapp, whatsappOficial } = profileOptions ?? profile.options
    const isWindows = navigator.userAgent.includes('Windows')
    let sendMobile: boolean = false

    if (linkWhatsapp === true || linkWhatsapp === undefined) {
      sendMobile = true
    }

    const currMotoboy = motoboys.find((moto) => moto.id === cart.motoboyId)

    if (
      (whatsappOficial || possibleMobile) &&
      sendMobile &&
      !isWindows &&
      button.name !== 'motoboy'
    ) {
      window.open(
        `whatsapp://send?phone=${i18n.t('ddi')}${cart.client.whatsapp}&text=${encodeTextURL(
          cart.client.name,
          phoneMessage.replaceAll('[NOME]', cart.client.name)
        )}`
      )
      buttonRef.current.disabled = false
      return
    }

    if (
      (whatsappOficial || possibleMobile) &&
      sendMobile &&
      !isWindows &&
      button.name === 'motoboy' &&
      currMotoboy
    ) {
      const message = encodeURIComponent(phoneMessage)
      window.open(
        `whatsapp://send?phone=${i18n.t('ddi')}${currMotoboy.whatsapp.replace(/\D/g, '')}&text=${message}`
      )
      buttonRef.current.disabled = false
      return
    }

    if (!('isElectron' in window) && button.name !== 'motoboy') {
      window.open(
        `https://wa.me/send?phone=${i18n.t('ddi')}${cart.client.whatsapp}&text=${encodeTextURL(cart.client.name, phoneMessage)}`
      )
      buttonRef.current.disabled = false
      return
    }

    if (
      currMotoboy &&
      !('isElectron' in window) &&
      button.name === 'motoboy'
    ) {
      const message = encodeURIComponent(phoneMessage)
      window.open(
        `https://wa.me/send?phone=${i18n.t('ddi')}${currMotoboy.whatsapp.replace(/\D/g, '')}&text=${message}`
      )
      buttonRef.current.disabled = false
      return
    }

    if ('isElectron' in window) {
      const contact = cart.client?.controls?.whatsapp?.contactId
        ? cart.client?.controls?.whatsapp?.contactId.user
        : `${i18n.t('ddi')}${cart.client.whatsapp}`

      sendMessage(
        contact,
        phoneMessage.replaceAll('[NOME]', cart.client.name)
      )

      onMessageSend((client: any) => {
        cart.client = client
        setCart(new Cart(cart))
      })
      return
    }
  }

  const handleSendMessage = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (isClicked) return
    setIsClicked(true)

    const currMotoboy = motoboys.find((moto) => moto.id === cart.motoboyId)

    const calcTaxOrDisc = (
      addon: ProfileFormPayment['addon'],
      price: number
    ) => {
      if (!addon) {
        return 0
      }

      let { type, value, valueType } = addon
      value = Number(value)
      if (value <= 0) {
        return 0
      }

      switch (valueType) {
        case 'fee':
          if (type === 'fixed') {
            return value
          } else {
            return (value / 100) * price
          }
        case 'discount':
          if (type === 'fixed') {
            return 0 - value
          } else {
            return 0 - (value / 100) * price
          }
        default:
          return 0
      }
    }

    const getFullAddress = ({ street, number, neighborhood, city, uf, zipcode }:
      { street?: string, number?: string, neighborhood?: string, city?: string, uf?: string, zipcode?: string }) => {
      const address = [street, number === 'SN' ? '' : number, neighborhood, city, uf, zipcode].filter(Boolean).join(', ')
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    }

    const fullAdress = cart.address && cart.addressId
      ? getFullAddress({
          street: cart?.address?.street,
          number: cart?.address?.number,
          neighborhood: cart?.address?.neighborhood,
          city: cart?.address?.city,
          uf: cart?.address?.uf,
          zipcode: cart?.address?.zipcode
        })
      : ''

    const change: number = Number(
      (
        Number(
          cart.formsPayment.find((pay) => pay.label === i18n.t('money'))?.change
        ) ?? 0
      ).toFixed(2)
    )

    const { addon } = cart?.formsPayment[0] ?? {}
    const taxValue: number = calcTaxOrDisc(addon, Number(cart.total))
    const cashbackValue: number = cart.formsPayment.some(
      (pay) => pay.label === 'Cashback'
    )
      ? Number(cart.formsPayment.find((pay) => pay.label === 'Cashback')?.value)
      : 0

    let coupomValue: number = 0
    if (cart.cupom) {
      const { type, value } = cart.cupom
      if (type === 'value') {
        coupomValue = Number(value)
      }
      if (type === 'percent') {
        coupomValue = Number(cart.total) * (Number(value) / 100)
      }
    }

    const finalPrice: number = Number(
      (
        Number(cart.total) +
        Number(cart.taxDelivery) +
        Number(taxValue) -
        Number(cashbackValue) -
        Number(coupomValue)
      ).toFixed(2)
    )
    console.log('MENSAGEM PRO BOY?',{ADDRESS: cart.address, addressId: cart.addressId});

    const motoboyDefaultMessage = ({cartOrigin}: {cartOrigin: 'ifood' | 'whatsmenu' | 'keeta' | 'nine-nine'}) => {
      console.log({cartOrigin, cartItens: cart.itens});

      if (cartOrigin === 'ifood') {
        return cart.address
        ? `*${profile.name}*
${i18n.t('hello')} *${currMotoboy?.name}* ${i18n.t('the_order_is_now_ready')} *${cart.client.name}*, ${i18n.t('if_you_need')}:
+${i18n.t('ddi')}${cart.client.whatsapp}
*${i18n.t('order_coder')}:* wm${cart.code}-${cart.type}
${cart.itens
  .map((item) => {
    return `${item.quantity}x - ${item.name} (${currency({ value: Number((Number(item.quantity) * Number(item.details.value)).toFixed(2)) })})
(OBS: ${item.obs !== '' ? item.obs : `_${i18n.t('not_informed')}_`})\n
${
  item.details.complements.length > 0
    ? `${i18n.t('complements')}:
${item.details.complements.map((complement) => complement.itens?.map((item) => `${item.quantity}x - ${item.name} (${currency({ value: Number((Number(item.quantity) * Number(item.value)).toFixed(2)) })})`).join('\n')).join('\n')}\n\n`
    : ''
}`
  })
  .join('')}
________________________________
*${i18n.t('package')}:* ${currency({ value: Number(cart.total?.toFixed(2)) })}
*${i18n.t('delivery')}:* ${cart.taxDelivery && cart.taxDelivery > 0 && typeof cart.taxDelivery === 'number' ? `${currency({ value: Number(cart.taxDelivery.toFixed(2)) })}` : i18n.t('free')}
${coupomValue > 0 ? `*${i18n.t('coupon')}:* ${currency({ value: Number(coupomValue.toFixed(2)) })}` : ''}
${cart.getTotalValue('addon') > 0 ? `*${i18n.t('addition')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${cart.getTotalValue('addon') < 0 ? `*${i18n.t('discont')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${
  taxValue === 0
    ? ''
    : taxValue > 0
    ? `*${i18n.t('serivce_fee')}:* ${currency({ value: Number(taxValue.toFixed(2)) })}`
    : `*${i18n.t('discount')}:* ${currency({ value: Number(-taxValue.toFixed(2)) })}`
}
${cashbackValue > 0 ? `*Cashback:* ${currency({ value: Number(cashbackValue.toFixed(2)) })}` : ''}
*Total:* ${currency({ value: Number(cart.getTotalValue('total').toFixed(2)) })}
*${i18n.t('payments_in')}:* ${cart.formsPayment.map((pay) => pay.label).join(', ')}
${
  cart.formsPayment.some((pay) => pay.label !== i18n.t('money')) &&
  isNaN(change)
    ? ''
    : change > 0
    ? `*${i18n.t('change_for')}:* ${currency({ value: Number(change?.toFixed(2)) })}
*${i18n.t('change')}:* ${currency({ value: Number((change - finalPrice)?.toFixed(2)) })}`
    : `*${i18n.t('no_change_needed')}*`
}
*${i18n.t('customer_delivery_address')}:*
*${i18n.t('street')}:* ${cart.address.street}
*Nº:* ${cart.address.number}
*${i18n.t('complement')}:* ${cart.address.complement ? cart.address.complement : i18n.t('not_informed')}
*${i18n.t('neighborhood')}:* ${cart.address.neighborhood}
*${i18n.t('reference')}:* ${cart.address.reference ? cart.address.reference : i18n.t('not_informed')}
*${i18n.t('city')}:* ${cart.address.city}
*${i18n.t('route_up')}:*
${fullAdress}
*${i18n.t('technology')}*
www.whatsmenu.com.br`
        : ''
      }

      if (cartOrigin === 'nine-nine') {
        return cart.address
        ? `*${profile.name}*
${i18n.t('hello')} *${currMotoboy?.name}* ${i18n.t('the_order_is_now_ready')} *${cart.client.name}*, ${i18n.t('if_you_need')}:
+${i18n.t('ddi')}${cart.client.whatsapp}
*${i18n.t('order_coder')}:* wm${cart.code}-${cart.type}
${cart.itens
  .map((item) => {
    return `${item.quantity}x - ${item.name} (${currency({ value: Number((Number(item.quantity) * Number(item.details.value)).toFixed(2)) })})
(OBS: ${item.obs !== '' ? item.obs : `_${i18n.t('not_informed')}_`})\n
${
  item.details.complements.length > 0
    ? `${i18n.t('complements')}:
${item.details.complements.map((complement) => complement.itens?.map((item) => `${item.quantity}x - ${item.name} (${currency({ value: Number((Number(item.quantity) * Number(item.value)).toFixed(2)) })})`).join('\n')).join('\n')}\n\n`
    : ''
}`
  })
  .join('')}
________________________________
*${i18n.t('package')}:* ${currency({ value: Number(cart.total?.toFixed(2)) })}
*${i18n.t('delivery')}:* ${cart.taxDelivery && cart.taxDelivery > 0 && typeof cart.taxDelivery === 'number' ? `${currency({ value: Number(cart.taxDelivery.toFixed(2)) })}` : i18n.t('free')}
${coupomValue > 0 ? `*${i18n.t('coupon')}:* ${currency({ value: Number(coupomValue.toFixed(2)) })}` : ''}
${cart.getTotalValue('addon') > 0 ? `*${i18n.t('addition')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${cart.getTotalValue('addon') < 0 ? `*${i18n.t('discont')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${
  taxValue === 0
    ? ''
    : taxValue > 0
    ? `*${i18n.t('serivce_fee')}:* ${currency({ value: Number(taxValue.toFixed(2)) })}`
    : `*${i18n.t('discount')}:* ${currency({ value: Number(-taxValue.toFixed(2)) })}`
}
${cashbackValue > 0 ? `*Cashback:* ${currency({ value: Number(cashbackValue.toFixed(2)) })}` : ''}
*Total:* ${currency({ value: Number(cart.getTotalValue('total').toFixed(2)) })}
*${i18n.t('payments_in')}:* ${cart.formsPayment.map((pay) => pay.label).join(', ')}
${
  cart.formsPayment.some((pay) => pay.label !== i18n.t('money')) &&
  isNaN(change)
    ? ''
    : change > 0
    ? `*${i18n.t('change_for')}:* ${currency({ value: Number(change?.toFixed(2)) })}
*${i18n.t('change')}:* ${currency({ value: Number((change - finalPrice)?.toFixed(2)) })}`
    : `*${i18n.t('no_change_needed')}*`
}
*${i18n.t('customer_delivery_address')}:*
*${i18n.t('street')}:* ${cart.address.street}
*Nº:* ${cart.address.number}
*${i18n.t('complement')}:* ${cart.address.complement ? cart.address.complement : i18n.t('not_informed')}
*${i18n.t('neighborhood')}:* ${cart.address.neighborhood}
*${i18n.t('reference')}:* ${cart.address.reference ? cart.address.reference : i18n.t('not_informed')}
*${i18n.t('city')}:* ${cart.address.city}
*${i18n.t('route_up')}:*
${fullAdress}
*${i18n.t('technology')}*
www.whatsmenu.com.br`
        : ''
      }

      if (cartOrigin === 'keeta') {
        keetaOrderMessageForMotoboy({cart})
      }

      if (cartOrigin === 'whatsmenu') {
        return cart.address && cart.addressId
        ? `*${profile.name}*
${i18n.t('hello')} *${currMotoboy?.name}* ${i18n.t('the_order_is_now_ready')} *${cart.client.name}*, ${i18n.t('if_you_need')}:
+${i18n.t('ddi')}${cart.client.whatsapp}
*${i18n.t('order_coder')}:* wm${cart.code}-${cart.type}
${cart.itens
  .map((item) => {
    return `${item.quantity}x - ${item.name} (${currency({ value: Number((Number(item.quantity) * Number(item.details.value)).toFixed(2)) })})
(OBS: ${item.obs !== '' ? item.obs : `_${i18n.t('not_informed')}_`})\n
${
  item.details.complements.length > 0
    ? `${i18n.t('complements')}:
${item.details.complements.map((complement) => complement.itens.map((item) => `${item.quantity}x - ${item.name} (${currency({ value: Number((Number(item.quantity) * Number(item.value)).toFixed(2)) })})`).join('\n')).join('\n')}\n\n`
    : ''
}`
  })
  .join('')}
________________________________
*${i18n.t('package')}:* ${currency({ value: Number(cart.total?.toFixed(2)) })}
*${i18n.t('delivery')}:* ${cart.taxDelivery && cart.taxDelivery > 0 && typeof cart.taxDelivery === 'number' ? `${currency({ value: Number(cart.taxDelivery.toFixed(2)) })}` : i18n.t('free')}
${coupomValue > 0 ? `*${i18n.t('coupon')}:* ${currency({ value: Number(coupomValue.toFixed(2)) })}` : ''}
${cart.getTotalValue('addon') > 0 ? `*${i18n.t('addition')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${cart.getTotalValue('addon') < 0 ? `*${i18n.t('discont')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${
  taxValue === 0
    ? ''
    : taxValue > 0
    ? `*${i18n.t('serivce_fee')}:* ${currency({ value: Number(taxValue.toFixed(2)) })}`
    : `*${i18n.t('discount')}:* ${currency({ value: Number(-taxValue.toFixed(2)) })}`
}
${cashbackValue > 0 ? `*Cashback:* ${currency({ value: Number(cashbackValue.toFixed(2)) })}` : ''}
*Total:* ${currency({ value: Number(cart.getTotalValue('total').toFixed(2)) })}
*${i18n.t('payments_in')}:* ${cart.formsPayment.map((pay) => pay.label).join(', ')}
${
  cart.formsPayment.some((pay) => pay.label !== i18n.t('money')) &&
  isNaN(change)
    ? ''
    : change > 0
    ? `*${i18n.t('change_for')}:* ${currency({ value: Number(change?.toFixed(2)) })}
*${i18n.t('change')}:* ${currency({ value: Number((change - finalPrice)?.toFixed(2)) })}`
    : `*${i18n.t('no_change_needed')}*`
}
*${i18n.t('customer_delivery_address')}:*
*${i18n.t('street')}:* ${cart.address.street}
*Nº:* ${cart.address.number}
*${i18n.t('complement')}:* ${cart.address.complement ? cart.address.complement : i18n.t('not_informed')}
*${i18n.t('neighborhood')}:* ${cart.address.neighborhood}
*${i18n.t('reference')}:* ${cart.address.reference ? cart.address.reference : i18n.t('not_informed')}
*${i18n.t('city')}:* ${cart.address.city}
*${i18n.t('route_up')}:*
${fullAdress}
*${i18n.t('technology')}*
www.whatsmenu.com.br`
        : ''
      }
      return ''
    }

    const keetaOrderMessageForMotoboy = ({cart}: {cart: any}) => {
      return cart.address
        ? `*${profile.name}*
${i18n.t('hello')} *${currMotoboy?.name}* ${i18n.t('the_order_is_now_ready')} *${cart.client.name}*, ${i18n.t('if_you_need')}:
+${i18n.t('ddi')}${cart.client.whatsapp}
*${i18n.t('order_coder')}:* wm${cart.code}-${cart.type}
${cart.itens
  .map((item: any) => {
    return `${item.quantity}x - ${item.name} (${currency({ value: Number((Number(item.quantity) * Number(item.details.value)).toFixed(2)) })})
(OBS: ${item.obs !== '' ? item.obs : `_${i18n.t('not_informed')}_`})\n
${
  item.details.complements.length > 0
    ? `${i18n.t('complements')}:
    ${item.details.complements.map((complement: any) => `${complement.quantity}x - ${complement.name} (${currency({ value: Number((Number(complement.quantity) * Number(complement.value)).toFixed(2)) })})`).join('\n')} \n\n`
    : ''}`
  })
  .join('')}
________________________________
*${i18n.t('package')}:* ${currency({ value: Number(cart.total?.toFixed(2)) })}
*${i18n.t('delivery')}:* ${cart.taxDelivery && cart.taxDelivery > 0 && typeof cart.taxDelivery === 'number' ? `${currency({ value: Number(cart.taxDelivery.toFixed(2)) })}` : i18n.t('free')}
${coupomValue > 0 ? `*${i18n.t('coupon')}:* ${currency({ value: Number(coupomValue.toFixed(2)) })}` : ''}
${cart.getTotalValue('addon') > 0 ? `*${i18n.t('addition')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${cart.getTotalValue('addon') < 0 ? `*${i18n.t('discont')}:* ${currency({ value: Number(cart.getTotalValue('addon').toFixed(2)) })}` : ``}
${
  taxValue === 0
    ? ''
    : taxValue > 0
    ? `*${i18n.t('serivce_fee')}:* ${currency({ value: Number(taxValue.toFixed(2)) })}`
    : `*${i18n.t('discount')}:* ${currency({ value: Number(-taxValue.toFixed(2)) })}`
}
${cashbackValue > 0 ? `*Cashback:* ${currency({ value: Number(cashbackValue.toFixed(2)) })}` : ''}
*Total:* ${currency({ value: Number(cart.getTotalValue('total').toFixed(2)) })}
*${i18n.t('payments_in')}:* ${cart.formsPayment.map((pay: any) => pay.label).join(', ')}
${
  cart.formsPayment.some((pay: any) => pay.label !== i18n.t('money')) &&
  isNaN(change)
    ? ''
    : change > 0
    ? `*${i18n.t('change_for')}:* ${currency({ value: Number(change?.toFixed(2)) })}
*${i18n.t('change')}:* ${currency({ value: Number((change - finalPrice)?.toFixed(2)) })}`
    : `*${i18n.t('no_change_needed')}*`
}
*${i18n.t('customer_delivery_address')}:*
*${i18n.t('street')}:* ${cart.address.street}
*Nº:* ${cart.address.number}
*${i18n.t('complement')}:* ${cart.address.complement ? cart.address.complement : i18n.t('not_informed')}
*${i18n.t('neighborhood')}:* ${cart.address.neighborhood}
*${i18n.t('reference')}:* ${cart.address.reference ? cart.address.reference : i18n.t('not_informed')}
*${i18n.t('city')}:* ${cart.address.city}
*${i18n.t('route_up')}:*
${fullAdress}
*${i18n.t('technology')}*
www.whatsmenu.com.br`
        : ''
    }

    if (buttonRef.current) {
      let phoneMessage = message
      buttonRef.current.disabled = true

      // Verifica se é pedido sem slug e status canceled
      if (!cart.slug && newStatus === 'canceled') {
        setShowCancelNoSlugModal(true)
        buttonRef.current.disabled = false
        setIsClicked(false)
        return
      }

      if (newStatus === 'canceled' && cart.controls?.mottu?.orderId) {
        setModalCancelDeliveryMottu(true)
      }

      const sendToWhatsApp = async () => {
        if (buttonRef.current) {
          const { linkWhatsapp, whatsappOficial } =
            profileOptions ?? profile.options
          const isWindows = navigator.userAgent.includes('Windows')
          let sendMobile: boolean = false

          if (linkWhatsapp === true || linkWhatsapp === undefined) {
            sendMobile = true
          }

          if (whatsappOficial) {
            const clientContact = cart.client?.controls?.whatsapp?.contactId
              ? cart.client?.controls?.whatsapp?.contactId.user
              : `${i18n.t('ddi')}${cart.client.whatsapp}`

            // Verificar se há template disponível para este status
            const statusKey = newStatus as keyof StatusTemplates
            const template = statusKey && statusTemplates[statusKey]

            if (template && ['production', 'transport', 'delivered', 'canceled'].includes(statusKey) && template.status === 'APPROVED') {
              // Enviar via template aprovado
              await api.post('/dashboard/integrations/whatsapp/meta/messages', {
                to: clientContact,
                type: 'template',
                template: template.name,
                language: template.language || 'pt_BR',
                components: [{
                  type: 'body',
                  parameters: [{ type: 'text', text: cart.client.name }]
                }]
              })
            } else if (template && template.status !== 'APPROVED') {
              // Log do status do template
              console.warn(`Template ${template.name} não aprovado (status: ${template.status}). Usando mensagem de texto.`)
              // Fallback para mensagem de texto
              await api.post('/dashboard/integrations/whatsapp/meta/messages', {
                to: clientContact,
                type: 'text',
                text: phoneMessage.replaceAll('[NOME]', cart.client.name),
              })
            } else {
              // Fallback para mensagem de texto se não houver template
              await api.post('/dashboard/integrations/whatsapp/meta/messages', {
                to: clientContact,
                type: 'text',
                text: phoneMessage.replaceAll('[NOME]', cart.client.name),
              })
            }

            if (currMotoboy && cart.type !== 'T' && newStatus === 'transport') {
              await api.post('/dashboard/integrations/whatsapp/meta/messages', {
                to: `${i18n.t('ddi')}${currMotoboy.whatsapp.replace(/\D/g, '')}`,
                type: 'text',
                text: motoboyDefaultMessage({cartOrigin: cart.origin as any}),
              })
            }

            buttonRef.current.disabled = false
            return
          }

          if (
            (whatsappOficial || possibleMobile) &&
            sendMobile &&
            !isWindows &&
            button.name !== 'motoboy'
          ) {
            window.open(
              `whatsapp://send?phone=${i18n.t('ddi')}${cart.client.whatsapp}&text=${encodeTextURL(
                cart.client.name,
                phoneMessage.replaceAll('[NOME]', cart.client.name)
              )}`
            )

            buttonRef.current.disabled = false
            return
          }

          if (
            (whatsappOficial || possibleMobile) &&
            sendMobile &&
            !isWindows &&
            button.name === 'motoboy'
          ) {
            const message = encodeURIComponent(motoboyDefaultMessage({cartOrigin: cart.origin as any}))
            window.open(
              `whatsapp://send?phone=${i18n.t('ddi')}${currMotoboy.whatsapp.replace(/\D/g, '')}&text=${message}`
            )

            buttonRef.current.disabled = false
            return
          }

          if (!('isElectron' in window) && button.name !== 'motoboy') {
            window.open(
              `https://wa.me/send?phone=${i18n.t('ddi')}${cart.client.whatsapp}&text=${encodeTextURL(cart.client.name, phoneMessage)}`
            )

            buttonRef.current.disabled = false
            return
          }

          if (
            currMotoboy &&
            !('isElectron' in window) &&
            button.name === 'motoboy'
          ) {
            const message = encodeURIComponent(motoboyDefaultMessage({cartOrigin: cart.origin as any}))
            window.open(
              `https://wa.me/send?phone=${i18n.t('ddi')}${currMotoboy.whatsapp.replace(/\D/g, '')}&text=${message}`
            )

            buttonRef.current.disabled = false
            return
          }

          if ('isElectron' in window) {
            const contact = cart.client?.controls?.whatsapp?.contactId
              ? cart.client?.controls?.whatsapp?.contactId.user
              : `${i18n.t('ddi')}${cart.client.whatsapp}`

            sendMessage(
              contact,
              phoneMessage.replaceAll('[NOME]', cart.client.name)
            )

            if (currMotoboy && cart.type !== 'T' && newStatus === 'transport') {
              sendMessage(
                `${i18n.t('ddi')}${currMotoboy.whatsapp.replace(/\D/g, '')}`,
                motoboyDefaultMessage({cartOrigin: cart.origin as any})
              )
            }

            onMessageSend((client: any) => {
              cart.client = client
              setCart(new Cart(cart))
            })
            return
          }
        }
      }

      if(button.name === 'Falar com Cliente') {
        await sendToWhatsApp()
      }

      if (newStatus || newStatus === null) {
        const waitMillis = localStorage.getItem('waitMillis')
          ? Number(localStorage.getItem('waitMillis'))
          : 5000

        const oldStatus = cart.status

        console.log('Status antigo:', oldStatus, 'Status novo:', newStatus)
        if (newStatus !== null) {
          switch (newStatus) {
            case 'production':
              phoneMessage =
                (profileOptions ?? profile.options).placeholders
                  .statusProduction ??
                (cart.type === 'P'
                  ? cart.defaultStatusProductionPackage
                  : cart.defaultStatusProductionMessage)
              setMessage(
                (profileOptions ?? profile.options).placeholders
                  .statusProduction ??
                  (cart.type === 'P'
                    ? cart.defaultStatusProductionPackage
                    : cart.defaultStatusProductionMessage)
              )

              break
            case 'transport':
              if (cart.address) {
                phoneMessage =
                  (profileOptions ?? profile.options).placeholders.statusSend ||
                  cart.defaultStatusTransportMessage
                setMessage(
                  (profileOptions ?? profile.options).placeholders.statusSend ||
                    cart.defaultStatusTransportMessage
                )
              } else {
                phoneMessage =
                  (profileOptions ?? profile.options).placeholders
                    .statusToRemove || cart.defaultStatusToRemoveMessage
                setMessage(
                  (profileOptions ?? profile.options).placeholders
                    .statusToRemove || cart.defaultStatusToRemoveMessage
                )
              }

              break
            case 'motoboy':
              if (!('isElectron' in window)) {
                phoneMessage = motoboyDefaultMessage({cartOrigin: cart.origin as any})
                setMessage(motoboyDefaultMessage({cartOrigin: cart.origin as any}))
              } else {
                phoneMessage = motoboyDefaultMessage({cartOrigin: cart.origin as any})
                setMessage(motoboyDefaultMessage({cartOrigin: cart.origin as any}))
              }

              break
            case 'canceled':
              console.log('Cancelamento detectado, verificando integrações...', cart.controls.entregas_expressas)
              if (cart.origin === 'ifood' || cart.controls.ifood?.orderId) {
                setShowReasons(true)
              }

              if (cart.controls.entregas_expressas?.order && profile.options.integrations.entregasexpressas) {
                authConfig(profile.options.integrations.entregasexpressas.api_key)
                await cancelOrder(cart.controls.entregas_expressas.order.id)
              }

              phoneMessage = canceledMessage(profile.options.locale.language)
              setMessage(canceledMessage(profile.options.locale.language))
              break
            default:
              phoneMessage = (profileOptions ?? profile.options).placeholders
                .clientText
              setMessage(
                (profileOptions ?? profile.options).placeholders.clientText
              )

              break
          }
        }
        if (
          cart.print ||
          ('isElectron' in window) ||
          profile.options.print.app ||
          cart.type === 'P' ||
          cart.origin === 'ifood'
        ) {
          if (
            door &&
            DateTime.local().toMillis() - lastRequestDate > waitMillis
          ) {
            try {
              if (cart.controls?.mottu?.orderId) {
                try {
                  await api.get(
                    `/dashboard/mottu/readyToPickup/${cart.controls?.mottu.orderId}`
                  )

                  handleShowToast({
                    type: 'success',
                    content: 'Motoboy chamado com sucesso',
                    title: 'Pronto para retirada',
                  })
                } catch (error) {
                  handleShowToast({
                    type: 'erro',
                    content: 'Não foi possível chamar o motoboy',
                    title: 'Pronto para retirada',
                  })
                  throw error
                }
              }

              setLoading && setLoading(true)

              if (newStatus !== 'motoboy') {
                await cart.updateStatus(newStatus, session)
              }

              if (cart.type === 'P') {
                setRequestsToPackage({ ...requestsToPackage })
              }

              if (cart.type !== 'T' && newStatus) {
                if (
                  oldStatus !== newStatus &&
                  oldStatus !== 'canceled' &&
                  newStatus !== 'delivered'
                ) {
                  await sendToWhatsApp()
                }
              }
            } catch (error) {
              throw error
            } finally {
              buttonRef.current.disabled = false
              setLoading && setLoading(false)
              setCarts([...carts])
            }
          } else {
            handleShowToast({
              show: true,
              title: 'Aguarde',
              content: 'Um pedido esta sendo impresso, aguarde a finalização',
            })
          }
        } else {
          if (cart.type !== 'T' && newStatus !== null) {
            await sendToWhatsApp()
          }
        }
      }
    }
  }

  const handleCloseModal = () => {
    // Perform any necessary logic here
    setShowModalNineNine(false)
  }

  return (
    <>
      {modalCancelDeliveryMottu && (
        <ModalCancelOrderMottu
          show={modalCancelDeliveryMottu}
          setShow={setModalCancelDeliveryMottu}
          cart={cart}
          taxMessage={taxmessageCancelMottu}
          setSelectedOption={setTaxmessageCancelMottu}
        />
      )}

      <CancelPdvModal
        show={showCancelNoSlugModal}
        setShow={setShowCancelNoSlugModal}
        onConfirmWithMessage={handleCancelWithMessage}
        onConfirmWithoutMessage={handleCancelWithoutMessage}
      />

      <RefuseOrderModal
        show={showReasons}
        reasons={reasons}
        cart={cart}
        onHide={() => setShowReasons(false)}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onCancelOrder={(data) => {
          cart.status = 'canceled'
          setCart(cart)
          setShowReasons(false)
          setNewPlacedOrders((prev) => ({
            ...prev,
            orders: prev.orders.filter(
              (order: any) => order.orderId !== data.order.orderId
            ),
          }))
        }}
      />
      <NineNineOrderCancelModal
        cart={cart}
        closeModal={handleCloseModal}
        openModal={showModalNineNine}
      />

      <AsaasRefundConfirm
        show={showRefundConfirmModal}
        onHide={async () => {
          setShowRefundConfirmModal(false)
          await processCancellation(pendingCancellationSkipMessage, false)
        }}
        loading={loading}
        cartCode={cart.code}
        onConfirm={async () => {
          try {
            setLoading(true)
            await processCancellation(pendingCancellationSkipMessage, true)
            setShowRefundConfirmModal(false)
          } catch (error) {
            console.error('Erro ao processar cancelamento com estorno:', error)
            handleShowToast({
              type: 'erro',
              title: 'Erro',
              content: 'Erro ao processar o cancelamento',
            })
          } finally {
            setLoading(false)
          }
        }}
      />

      <Button
        ref={buttonRef}
        onClick={
      handleChangeStatus}
        {...button.props}
        disabled={loading}
      >
        {loading ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
            {' '}
            {i18n.t('please_wait_n')}
          </>
        ) : button.name === 'motoboy' ? (
          <GiFullMotorcycleHelmet size={24} />
        ) : (
          button.name
        )}
        {rest.children}
      </Button>
    </>
  )
}
